"use client";

import { useState, useEffect, useRef } from "react";

import { useDebug } from "./useDebug";

export type AudioRecordingHandlers = {
  onStop?: () => void;
  onStart?: () => void;
  onDataAvailable?: (data: Blob) => void;
  onVolume?: (volume: number) => void;
};

/**
 * Hook for recording voice input and sending it to the server for transcription.
 */
export const useAudioRecording = (handlers: AudioRecordingHandlers = {}) => {
  const [isRecording, setIsRecording] = useState(false);
  const { log } = useDebug();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const analyserNode = useRef<AnalyserNode | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const volumeDataArray = useRef<Uint8Array | null>(null);

  // Destruct audio recorder tracks on unmount
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      stopAllTracks();
    };
  }, []);

  const stopAllTracks = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  };

  const setupAudioAnalysis = (stream: MediaStream) => {
    audioContext.current = new AudioContext();
    analyserNode.current = audioContext.current.createAnalyser();
    analyserNode.current.fftSize = 256;

    const source = audioContext.current.createMediaStreamSource(stream);
    source.connect(analyserNode.current);

    volumeDataArray.current = new Uint8Array(
      analyserNode.current.frequencyBinCount,
    );
  };

  const getAnalysedVolume = (): number => {
    if (!analyserNode.current || !volumeDataArray.current) {
      return 0;
    }

    // @ts-ignore - Uint8Array generic typing varies between TS versions
    analyserNode.current.getByteFrequencyData(volumeDataArray.current);
    const average =
      volumeDataArray.current.reduce(
        (acc: number, val: number) => acc + val,
        0,
      ) / volumeDataArray.current.length;

    return average;
  };

  useEffect(() => {
    if (!isRecording) {
      return;
    }

    const monitorVolume = () => {
      const volume = getAnalysedVolume();
      handlers?.onVolume?.(volume);
    };

    const intervalID = setInterval(monitorVolume, 100);

    return () => clearInterval(intervalID);
  }, [isRecording]);

  const startRecording = async () => {
    if (isRecording) {
      return;
    }

    log(
      "cyan",
      ["useAudioRecording", "startRecording"],
      "🎤 Recording audio...",
    );

    handlers.onStart?.();

    // Request microphone access and start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setupAudioAnalysis(stream);

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorder.ondataavailable = (event) => {
        handlers.onDataAvailable?.(event.data);
      };

      mediaRecorder.start(250); // Send audio chunks every 250ms
      mediaRecorderRef.current = mediaRecorder;

      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = () => {
    if (!isRecording) {
      return;
    }

    log("orange", ["useAudioRecording", "stopRecording"]);

    setIsRecording(false);

    // Stop recording and close resources
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      stopAllTracks();
    }

    // Clean up audio analysis
    if (audioContext.current) {
      audioContext.current.close();
      audioContext.current = null;
    }
    analyserNode.current = null;
    volumeDataArray.current = null;

    handlers.onStop?.();
  };

  return {
    isRecording,
    startRecording,
    stopRecording,
  };
};
